import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import FEATURE_FLAGS from "../config/featureFlags";
applyPlugin(jsPDF);

/**
 * Generate a professional health screening report PDF.
 * Designed to be shared with a healthcare provider.
 */
export function generateHealthReport(analysisResults, locale = "en") {
  const { surveyData, recommendations, healthScreening = [] } = analysisResults || {};
  const { stronglyRecommend = [], recommend = [] } = recommendations || {};

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let y = 20;

  const isZh = locale === "zh";

  // ─── Helper functions ───
  const addText = (text, x, yPos, options = {}) => {
    const { fontSize = 10, fontStyle = "normal", color = [51, 51, 51], maxWidth = pageWidth - 2 * margin } = options;
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", fontStyle);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, yPos);
    return yPos + lines.length * fontSize * 0.5;
  };

  const checkPageBreak = (needed) => {
    if (y + needed > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      y = 20;
    }
  };

  const addSectionHeader = (text) => {
    checkPageBreak(20);
    y += 6;
    doc.setDrawColor(47, 76, 33);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageWidth - margin, y);
    y += 8;
    y = addText(text, margin, y, { fontSize: 14, fontStyle: "bold", color: [47, 76, 33] });
    y += 4;
  };

  // ─── HEADER ───
  doc.setFillColor(47, 76, 33);
  doc.rect(0, 0, pageWidth, 35, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.text("Pebble Health Screening Report", margin, 18);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(isZh ? "个人健康筛查报告 — 供医疗服务提供者参考" : "Personalized Report — For Your Healthcare Provider", margin, 28);
  y = 45;

  // ─── REPORT METADATA ───
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  y = addText(`Report Generated: ${today}`, margin, y, { fontSize: 9, color: [120, 120, 120] });
  y = addText("This report is generated by Pebble Health (pebblehealth.ai) based on self-reported data.", margin, y + 2, { fontSize: 8, color: [150, 150, 150] });
  y += 6;

  // ─── PATIENT PROFILE ───
  addSectionHeader(isZh ? "个人资料" : "Patient Profile");

  const age = surveyData?.age || "—";
  const sex = surveyData?.biologicalSex || "—";
  const ethnicity = surveyData?.ethnicity || "—";
  const location = surveyData?.location || "—";
  const weightKg = surveyData?.weightInKg || surveyData?.weight || "—";
  const heightCm = surveyData?.heightInCm || surveyData?.height || "—";
  let bmiText = "—";
  if (typeof weightKg === "number" && typeof heightCm === "number" && heightCm > 0) {
    const bmi = (weightKg / ((heightCm / 100) ** 2)).toFixed(1);
    bmiText = `${bmi} kg/m²`;
  }

  const familyHistory = (surveyData?.familyCancerHistory || []).filter(f => f.toLowerCase() !== "none");

  const profileData = [
    [isZh ? "年龄" : "Age", String(age)],
    [isZh ? "性别" : "Biological Sex", String(sex)],
    [isZh ? "民族" : "Ethnicity", String(ethnicity)],
    [isZh ? "地区" : "Location", String(location)],
    [isZh ? "BMI" : "BMI", bmiText],
    [isZh ? "家族癌症史" : "Family Cancer History", familyHistory.length > 0 ? familyHistory.join(", ") : (isZh ? "无" : "None reported")],
  ];

  doc.autoTable({
    startY: y,
    head: [],
    body: profileData,
    theme: "plain",
    margin: { left: margin },
    styles: { fontSize: 10, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 50, textColor: [47, 76, 33] },
      1: { cellWidth: 100 },
    },
  });
  y = doc.lastAutoTable.finalY + 4;

  // ─── SELF-REPORTED HEALTH STATUS ───
  addSectionHeader(isZh ? "自述健康状况" : "Self-Reported Health Status");

  const healthStatus = [];
  if (FEATURE_FLAGS.IMPROVED_VITALS_FLOW) {
    if (surveyData?.bloodSugarStatus && surveyData.bloodSugarStatus !== "unsure") {
      healthStatus.push([isZh ? "血糖" : "Blood Sugar", formatStatus(surveyData.bloodSugarStatus)]);
    }
    if (surveyData?.cholesterolStatus && surveyData.cholesterolStatus !== "unsure") {
      healthStatus.push([isZh ? "胆固醇" : "Cholesterol", formatStatus(surveyData.cholesterolStatus)]);
    }
    if (surveyData?.bloodPressureStatus && surveyData.bloodPressureStatus !== "unsure") {
      healthStatus.push([isZh ? "血压" : "Blood Pressure", formatStatus(surveyData.bloodPressureStatus)]);
    }
  }
  const chronicList = (surveyData?.chronicDiseases || []).filter(d => d !== "none");
  if (chronicList.length > 0) {
    healthStatus.push([isZh ? "慢性疾病" : "Chronic Conditions", chronicList.join(", ")]);
  }
  const smokingYears = parseFloat(surveyData?.smokingHistory) || 0;
  const cigsPerDay = parseFloat(surveyData?.cigarettesPerDay) || 0;
  const packYears = smokingYears * (cigsPerDay / 20); // 1 pack = 20 cigarettes
  healthStatus.push([isZh ? "吸烟" : "Smoking", packYears > 0 ? `${packYears.toFixed(1)} pack-years (${cigsPerDay} cigs/day × ${smokingYears} years)` : (isZh ? "不吸烟" : "Non-smoker")]);
  healthStatus.push([isZh ? "饮酒" : "Alcohol", formatAlcohol(surveyData?.alcoholConsumption)]);

  if (healthStatus.length > 0) {
    doc.autoTable({
      startY: y,
      head: [],
      body: healthStatus,
      theme: "plain",
      margin: { left: margin },
      styles: { fontSize: 10, cellPadding: 2 },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 50, textColor: [47, 76, 33] },
        1: { cellWidth: 120 },
      },
    });
    y = doc.lastAutoTable.finalY + 4;
  }

  // ─── CANCER SCREENING RECOMMENDATIONS ───
  if (stronglyRecommend.length > 0 || recommend.length > 0) {
    addSectionHeader(isZh ? "癌症筛查建议" : "Cancer Screening Recommendations");

    const allRecs = [
      ...stronglyRecommend.map(r => ({ ...r, priority: isZh ? "强烈建议" : "Strongly Recommended" })),
      ...recommend.map(r => ({ ...r, priority: isZh ? "建议" : "Recommended" })),
    ];

    const cancerTableBody = allRecs.map(r => {
      const tierLabel = r.tier === "primary"
        ? (isZh ? "国家指南" : "National Guideline")
        : (isZh ? "个性化建议" : "Personalized");
      return [
        r.priority,
        r.cancer || r.screening?.label || r.label || "—",
        r.screening?.method || "—",
        r.screening?.interval || "—",
        `${(r.riskScore || 1).toFixed(1)}x`,
        tierLabel,
      ];
    });

    doc.autoTable({
      startY: y,
      head: [[
        isZh ? "优先级" : "Priority",
        isZh ? "筛查项目" : "Screening",
        isZh ? "方法" : "Method",
        isZh ? "频率" : "Interval",
        isZh ? "风险倍数" : "Risk",
        isZh ? "依据" : "Basis",
      ]],
      body: cancerTableBody,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [47, 76, 33], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 248, 244] },
    });
    y = doc.lastAutoTable.finalY + 4;

    // Risk factors for top recommendations
    const topRecs = allRecs.slice(0, 5);
    for (const rec of topRecs) {
      if (rec.riskFactors && rec.riskFactors.length > 0) {
        checkPageBreak(30);
        y = addText(`${rec.cancer || rec.screening?.label || rec.label || ""}:`, margin, y, { fontSize: 9, fontStyle: "bold" });
        for (const rf of rec.riskFactors) {
          checkPageBreak(10);
          y = addText(`  • ${rf.factor}: ${rf.multiplier}x`, margin + 4, y, { fontSize: 8, color: [80, 80, 80] });
        }
        y += 3;
      }
    }
  }

  // ─── CARDIOMETABOLIC SCREENING ───
  if (FEATURE_FLAGS.IMPROVED_VITALS_FLOW && healthScreening.length > 0) {
    addSectionHeader(isZh ? "心血管代谢筛查" : "Cardiometabolic Health Screening");

    for (const rec of healthScreening) {
      checkPageBreak(40);
      const riskLabel = { diagnosed: "Diagnosed", high: "High Risk", moderate: "Moderate Risk", low: "Low Risk" };
      y = addText(`${rec.icon} ${rec.category} — ${riskLabel[rec.riskLevel] || rec.riskLevel}`, margin, y, {
        fontSize: 11, fontStyle: "bold", color: rec.riskLevel === "high" || rec.riskLevel === "diagnosed" ? [220, 53, 69] : [47, 76, 33],
      });
      y += 2;

      if (rec.riskFactors.length > 0) {
        y = addText(`Risk factors: ${rec.riskFactors.join("; ")}`, margin + 4, y, { fontSize: 8, color: [100, 100, 100] });
      }

      y = addText(`Screening: ${rec.screening.method}`, margin + 4, y + 1, { fontSize: 9 });
      y = addText(`Interval: ${rec.screening.interval}`, margin + 4, y, { fontSize: 9 });
      y = addText(`Guideline: ${rec.screening.guideline}`, margin + 4, y, { fontSize: 8, color: [120, 120, 120] });
      y += 2;

      // Action items
      y = addText(isZh ? "建议措施:" : "Recommended Actions:", margin + 4, y, { fontSize: 9, fontStyle: "bold" });
      for (const item of rec.actionItems.slice(0, 4)) {
        checkPageBreak(8);
        y = addText(`  • ${item}`, margin + 8, y, { fontSize: 8 });
      }
      y += 4;
    }
  }

  // ─── FOOTER / DISCLAIMER ───
  checkPageBreak(40);
  y += 6;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageWidth - margin, y);
  y += 6;

  y = addText(
    isZh
      ? "重要声明：本报告基于自我报告的数据和循证临床指南（USPSTF、ACS、ADA、ACC/AHA）生成。仅供参考，不构成医疗诊断或治疗建议。请与您的医疗服务提供者讨论您的个人筛查计划。"
      : "IMPORTANT DISCLAIMER: This report is generated from self-reported data and evidence-based clinical guidelines (USPSTF, ACS, ADA, ACC/AHA). It is for informational purposes only and does not constitute medical diagnosis or treatment advice. Please discuss your personalized screening plan with your healthcare provider.",
    margin, y, { fontSize: 7, color: [130, 130, 130] }
  );
  y += 8;
  y = addText("Generated by Pebble Health — pebblehealth.ai | omnicareai@gmail.com", margin, y, { fontSize: 7, color: [150, 150, 150] });

  return doc;
}

/**
 * Save or share a PDF report with mobile-friendly fallbacks.
 * - On desktop: uses doc.save() (standard download)
 * - On mobile: creates a blob URL and uses an anchor element with download attribute
 * - If navigator.share is available (mobile), offers a share option
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {string} filename - Desired filename for the PDF
 * @param {object} options - { preferShare: boolean } - if true, prefer Web Share API on mobile
 * @returns {Promise<string>} - "downloaded" | "shared" | "opened"
 */
export async function savePdfMobileFriendly(doc, filename = "PebbleHealth-Report.pdf", options = {}) {
  const { preferShare = false } = options;
  const blob = doc.output("blob");

  // Try Web Share API first if preferred and available (mostly mobile)
  if (preferShare && navigator.share && navigator.canShare) {
    try {
      const file = new File([blob], filename, { type: "application/pdf" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: "Pebble Health Report",
          text: "My personalized health screening report from Pebble Health",
          files: [file],
        });
        return "shared";
      }
    } catch (err) {
      // User cancelled share or share failed — fall through to download
      if (err.name === "AbortError") return "shared"; // user cancelled, still counts
    }
  }

  // Mobile-friendly download: create blob URL + anchor element
  // This works better than doc.save() on iOS Safari and Android Chrome
  try {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();

    // Cleanup after a delay (some browsers need the element to persist briefly)
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    }, 1000);

    return "downloaded";
  } catch {
    // Last resort: open in new tab (works on all browsers including iOS Safari)
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    return "opened";
  }
}

/**
 * Detect if the current device is likely a mobile browser.
 */
export function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    || (navigator.maxTouchPoints > 0 && window.innerWidth < 768);
}

// ─── Helpers ───
function formatStatus(status) {
  const map = {
    normal: "Normal",
    prediabetic: "Pre-diabetic",
    diabetic: "Diabetic",
    borderline: "Borderline High",
    high: "High",
    elevated: "Elevated",
  };
  return map[status] || status || "—";
}

function formatAlcohol(val) {
  const map = {
    none: "None",
    under_7: "Light (< 7 drinks/week)",
    "7_to_14": "Moderate (7-14/week)",
    "14_to_25": "Heavy (14-25/week)",
    "25_to_50": "Very Heavy (25-50/week)",
    over_50: "Very Heavy (50+/week)",
  };
  return map[val] || val || "—";
}
